// React hook: live height of the bottom instrument-strip.
//
// Three floating bottom-bar components (NexradBar, SfcAnalysisBar,
// WxHazardBar) used to ship byte-identical 18-line useEffect blocks
// to measure the strip's rendered height. Consolidate.
//
// Why this matters: the instrument bar wraps to two rows on narrow
// phones, so its rendered height isn't fixed. Each floating bar
// stacks above it and needs the live height to compute its own
// `bottom` offset.
//
// Implementation:
//   * Look up the strip via the [data-fy-instrument-strip] data attr
//     (added by MapPage's DataStrip wrapper).
//   * Re-measure on (a) initial mount, (b) after 250ms + 800ms (the
//     GPS fix-first-render quirk shifts the strip by ~1px), (c)
//     window resize, and (d) any size change captured by a
//     ResizeObserver.
//   * `enabled=false` short-circuits the effect entirely so a hidden
//     bar doesn't waste a ResizeObserver.

import { useEffect, useState } from 'react';

const INITIAL_HEIGHT_PX = 56;
const REMEASURE_DELAYS_MS = [250, 800] as const;

export function useInstrumentStripHeight(enabled: boolean): number {
  const [height, setHeight] = useState<number>(INITIAL_HEIGHT_PX);

  useEffect(() => {
    if (!enabled) return;

    function measure() {
      const strip = document.querySelector<HTMLElement>('[data-fy-instrument-strip]');
      if (!strip) {
        setHeight(0);
        return;
      }
      setHeight(strip.getBoundingClientRect().height);
    }

    measure();

    const strip = document.querySelector<HTMLElement>('[data-fy-instrument-strip]');
    const ro = strip ? new ResizeObserver(measure) : null;
    if (strip && ro) ro.observe(strip);

    window.addEventListener('resize', measure);

    // The DataStrip's first GPS tick shifts its content size by a
    // pixel or two as live values populate. Re-measure shortly
    // after mount to catch it.
    const timers = REMEASURE_DELAYS_MS.map((ms) => window.setTimeout(measure, ms));

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [enabled]);

  return height;
}
